import {
    type MembershipProfileBillsAnswer,
    MembershipProfileCategory,
    type MembershipProfileIncomeAnswer,
    type MembershipProfileMoneyGoalAnswer,
    QuestionKey,
} from '@fruitful/member-profile';
import { BillActivationStatus } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { useGetAllBills } from '../../bills/hooks/useGetBills';
import { useGetUserHasPendingJobs } from '../../bills/hooks/useGetUserHasPendingJobs';
import { activeAndReviewBillsAtom } from '../../bills/state/BillsAtoms';
import { formatDollars } from '../../common/utils/bankingUtils';
import useGetHolisticSummary from '../../holistic/hooks/useGetHolisticSummary';
import { hasAnyExternalHolisticAccountsAtom } from '../../holistic/state/HolisticAtoms';
import useGetMembershipProfileAnswer from '../../member-profile/hooks/useGetMembershipProfileAnswers';
import { useGetLiabilities } from './useGetLiabilities';

export const useOnboardingCompleteness = () => {
    const { data: financialGoalsData, isLoading: isLoadingFinancialGoals } =
        useGetMembershipProfileAnswer<MembershipProfileMoneyGoalAnswer[]>({
            category: MembershipProfileCategory.FINANCIAL_GOALS,
            questionKey: QuestionKey.MONEY_GOALS_ONBOARDING_V2,
        });

    const { isLoading: isLoadingHolisticSummary } = useGetHolisticSummary();
    const hasAnyExternalHolisticAccounts = useAtomValue(
        hasAnyExternalHolisticAccountsAtom,
    );
    const activeAndReviewBills = useAtomValue(activeAndReviewBillsAtom);
    const { isLoadingAllBills } = useGetAllBills();
    const { userHasPendingJobs, isLoadingUserHasPendingJobs } =
        useGetUserHasPendingJobs();
    const { data: liabilitiesData, isLoading: isLoadingLiabilitiesData } =
        useGetLiabilities();

    const { data: incomeData, isLoading: isLoadingIncome } =
        useGetMembershipProfileAnswer<MembershipProfileIncomeAnswer>({
            category: MembershipProfileCategory.CASH_FLOW,
            questionKey: QuestionKey.INCOME,
        });

    const { data: billsData, isLoading: isLoadingBills } =
        useGetMembershipProfileAnswer<MembershipProfileBillsAnswer>({
            category: MembershipProfileCategory.CASH_FLOW,
            questionKey: QuestionKey.BILLS,
        });

    const isLoadingOnboardingCompleteness = useMemo(() => {
        return (
            isLoadingHolisticSummary ||
            isLoadingLiabilitiesData ||
            isLoadingAllBills ||
            isLoadingUserHasPendingJobs ||
            isLoadingIncome ||
            isLoadingBills
        );
    }, [
        isLoadingHolisticSummary,
        isLoadingLiabilitiesData,
        isLoadingAllBills,
        isLoadingUserHasPendingJobs,
        isLoadingIncome,
        isLoadingBills,
    ]);

    const isFinancialGoalsComplete = useMemo(
        () => !isLoadingFinancialGoals && financialGoalsData != null,
        [financialGoalsData, isLoadingFinancialGoals],
    );

    const isIncomeComplete = useMemo(
        () => !isLoadingIncome && incomeData != null,
        [incomeData, isLoadingIncome],
    );

    const membershipHasBillsPendingReview = useMemo(() => {
        return (activeAndReviewBills ?? []).some(
            (bill) => bill.activationStatus === BillActivationStatus.REVIEW,
        );
    }, [activeAndReviewBills]);

    const confirmedCreditCards = useMemo(() => {
        return liabilitiesData?.liabilities.creditCards.filter(
            (creditCard) =>
                creditCard.reviewStatus === BillActivationStatus.ACTIVE,
        );
    }, [liabilitiesData]);

    const creditCardMinimumsTotal = useMemo(() => {
        return (
            confirmedCreditCards?.reduce(
                (sum, cc) => sum + (cc.monthlyPayment || 0),
                0,
            ) ?? 0
        );
    }, [confirmedCreditCards]);

    const billsMonthlyTotal = useMemo(() => {
        if (membershipHasBillsPendingReview) {
            return null;
        }

        const regularBillsTotal = activeAndReviewBills.reduce(
            (acc, bill) => acc + (bill.monthlyAmount ?? 0),
            0,
        );

        const totalWithCreditCards =
            regularBillsTotal + creditCardMinimumsTotal;

        return formatDollars(totalWithCreditCards, false);
    }, [
        activeAndReviewBills,
        creditCardMinimumsTotal,
        membershipHasBillsPendingReview,
    ]);

    const billsAreFullyReviewed = useMemo(() => {
        return (
            (confirmedCreditCards ?? []).length + activeAndReviewBills.length >
                0 &&
            activeAndReviewBills.every((bill) =>
                [
                    BillActivationStatus.ACTIVE,
                    BillActivationStatus.REJECTED,
                    BillActivationStatus.REMOVED,
                ].includes(bill.activationStatus),
            )
        );
    }, [
        activeAndReviewBills,
        confirmedCreditCards,
        isLoadingUserHasPendingJobs,
        isLoadingAllBills,
    ]);

    const isBillsComplete = useMemo(() => {
        if (
            isLoadingHolisticSummary ||
            isLoadingLiabilitiesData ||
            isLoadingAllBills ||
            isLoadingBills
        ) {
            return false;
        }

        if (hasAnyExternalHolisticAccounts) {
            return (
                !userHasPendingJobs &&
                billsMonthlyTotal !== null &&
                billsAreFullyReviewed
            );
        }

        return billsData != null;
    }, [
        billsAreFullyReviewed,
        billsData,
        billsMonthlyTotal,
        hasAnyExternalHolisticAccounts,
        isLoadingAllBills,
        isLoadingBills,
        isLoadingHolisticSummary,
        isLoadingLiabilitiesData,
        userHasPendingJobs,
    ]);

    return useMemo(
        () => ({
            isLoadingOnboardingCompleteness,
            isFinancialGoalsComplete,
            isIncomeComplete,
            isBillsComplete,
        }),
        [
            isLoadingOnboardingCompleteness,
            isFinancialGoalsComplete,
            isIncomeComplete,
            isBillsComplete,
        ],
    );
};
